/* eslint-disable no-nested-ternary */
/* eslint-disable consistent-return */
import * as THREE from 'three';
import * as marietta from '../..';
import { ALMOST_ZERO, AREA_ZERO, ITEM_MODE_NO } from '../Constants';
import { Point } from '../../core';
import { PointCollection } from '../collection';

export const RC_CLSTAT = {
  PARALLEL: 1, /* ２直線は平行 */
  VERTICAL: 2, /* ２直線は垂直 */
  DIAGONAL: 3, /* ２直線は斜交 */
  ERROR: 4,
};

class WebCADUtil {
  /** これ以上の指の本数（＝マルチタッチ）を検知したら移動ドラッグを中断する閾値 */
  static readonly MULTI_TOUCH_FINGER_COUNT: number = 2;

  /** isOnLineで、topDotが端点と同一点とみなす距離のしきい値(mm) */
  static readonly IS_ON_LINE_ENDPOINT_DISTANCE_THRESHOLD: number = 0.01;

  /** isOnLineで、topDotが線上にあるとみなす距離誤差の許容値(mm) */
  static readonly IS_ON_LINE_DISTANCE_TOLERANCE: number = 0.5;

  /**
     * マウス／タッチイベントから画面座標(px)を取り出す
     * @param MouseEvent | TouchEvent
     * @return { x, y } 画面座標(px)
     */
  static getEventClientPoint(e: MouseEvent | TouchEvent): { x: number; y: number } {
    const touchEvent = e as TouchEvent;
    if (touchEvent.touches && touchEvent.touches.length > 0) {
      return { x: touchEvent.touches[0].clientX, y: touchEvent.touches[0].clientY };
    }
    if (touchEvent.changedTouches && touchEvent.changedTouches.length > 0) {
      return { x: touchEvent.changedTouches[0].clientX, y: touchEvent.changedTouches[0].clientY };
    }
    const mouseEvent = e as MouseEvent;
    return { x: mouseEvent.clientX, y: mouseEvent.clientY };
  }

  /**
     * ドラッグ制限の取得
     */
  static getDragLimits(): { minX: number, minY: number, maxX: number, maxY: number } {
    // 範囲
    const dragLimit = {
      minX: 0, minY: 0, maxX: marietta.webcad.WebCADGlobal.LAYER_SIZE_W, maxY: marietta.webcad.WebCADGlobal.LAYER_SIZE_H,
    };
    return dragLimit;
  }

  /**
     * イベントの座標を、ローカル座標への変換
     * @param MouseEvent
     * @return marietta.core.Point
     */
  static convertToLocalPoint(e: MouseEvent): marietta.core.Point {
    let retPoint: marietta.core.Point;
    if (marietta.webcad.WebCADGlobal.userAgentManager.isMobile || marietta.webcad.WebCADGlobal.userAgentManager.isTablet) {
      retPoint = new marietta.core.Point(e.pageX, e.pageY);
    } else {
      retPoint = new marietta.core.Point(e.clientX, e.clientY);
    }
    // メニューの幅分
    retPoint.x -= marietta.webcad.WebCADGlobal.OUTERFRAME_LEFT_FRAME_WIDTH;

    // ヘッダー分
    retPoint.y -= marietta.webcad.WebCADGlobal.OUTERFRAME_HEADER_HEIGHT;

    return retPoint;
  }

  /**
    * ピクセル幅から、mm幅を算出
    * @param Number 変換したいmmM数値
    * */
  static convertMMToPix(value: number): number {
    return (value / marietta.webcad.WebCADGlobal.webcad2Dmanager.modulePitch) * marietta.webcad.WebCADGlobal.PIX_IN_GRID;
  }

  /**
   * 座標をグリッド（モジュール補正のピッチ）上へ丸める。lockX/lockYの軸は丸めない。
   * グリッド割OFF・フリー寸法時は snapUnitPix が1を返すため実質無効になる。
   */
  static snapPointToGrid(p: marietta.core.Point, lockX: boolean = false, lockY: boolean = false): marietta.core.Point {
    const snapUnit: number = marietta.webcad.WebCADGlobal.webcad2Dmanager.snapUnitPix;
    return new marietta.core.Point(
      lockX ? p.x : Math.round(p.x / snapUnit) * snapUnit,
      lockY ? p.y : Math.round(p.y / snapUnit) * snapUnit,
    );
  }

  /**
     * ピクセル幅から、mm幅を算出
     * @param int 変換したいピクセル数値
     * */
  static convertPixToMM(value: number): number {
    return (value / marietta.webcad.WebCADGlobal.PIX_IN_GRID) * marietta.webcad.WebCADGlobal.webcad2Dmanager.modulePitch;
  }

  /**
     * ピクセル幅から、m幅を算出
     * @param int 変換したいピクセル数値
     * */
  static convertPixToM(value: number): number {
    return (((value / marietta.webcad.WebCADGlobal.PIX_IN_GRID)) * marietta.webcad.WebCADGlobal.webcad2Dmanager.modulePitch) / 1000;
  }

  /**
     * 面積計算
     * @param ドット一覧
     * @param 四捨五入するか否か。defaultは false
     * */
  static calculateArea(dotList, booRound: boolean = true): number {
    let areaSum: number = 0;
    let point1: marietta.core.Point;
    let point2: marietta.core.Point;
    let dotMC1: marietta.core.Point;
    let dotMC2: marietta.core.Point;

    /* 面積方程式に通す
        n
         1/2* Σ   ( X[k] - X[k+1])( Y[k] + Y[k+1])
        k=1
        */
    for (let i: number = 0; i < dotList.length; i += 1) {
      dotMC1 = dotList[i];
      dotMC2 = (i === dotList.length - 1) ? dotList[0] : dotList[i + 1];
      point1 = new marietta.core.Point(marietta.webcad.util.WebCADUtil.convertPixToM(dotMC1.x), marietta.webcad.util.WebCADUtil.convertPixToM(dotMC1.y));
      point2 = new marietta.core.Point(marietta.webcad.util.WebCADUtil.convertPixToM(dotMC2.x), marietta.webcad.util.WebCADUtil.convertPixToM(dotMC2.y));

      areaSum += (point1.x - point2.x) * (point1.y + point2.y);
    }
    if (booRound) {
      areaSum = Math.abs(Math.round((areaSum / 2) * 10) / 10);
    } else {
      areaSum = Number.parseFloat(Math.abs(areaSum / 2).toFixed(3));
    }

    return areaSum;
  }

  /**
     * 面積を帖単位に変換
     * @param ドット一覧
     * */
  static calculateAreaInTsubo(dotList): number {
    const heibei: number = marietta.webcad.util.WebCADUtil.calculateArea(dotList, false);
    const jyouUnit: number = marietta.webcad.WebCADGlobal.M_IN_JYO * (marietta.webcad.WebCADGlobal.M_IN_JYO * 2);
    let jyo: number = Math.round((heibei / jyouUnit) * 100) / 100; // 端数が出てしまうので、微調整を行う
    jyo = Math.round(jyo * 10) / 10;

    return jyo;
  }

  /**
     * 点が線分の近くにあるか否か
     * @param Point 線分の始点
     * @param Point 線分の終点
     * @param Point 点
     * @return Boolean
     */
  static isPointOnLineOrClose(p1: marietta.core.Point, p2: marietta.core.Point, hitPoint: marietta.core.Point, distance: number = 50): boolean {
    const crossPoint: marietta.core.Point = marietta.webcad.util.WebCADUtil.calculateCrossPoint(p1, p2, hitPoint);

    return (marietta.core.Point.distance(hitPoint, crossPoint) < distance);
  }

  /**
     * 辺と点の直行点を求める
     */
  static calculateCrossPoint(startDot: marietta.core.Point, endDot: marietta.core.Point, topDot: marietta.core.Point): marietta.core.Point {
    let y: number = endDot.y - startDot.y;
    let x: number = endDot.x - startDot.x;
    const d: number = x * x + y * y;
    if (d < 0.01) {
      return null;
    }
    const t: number = ((topDot.x - startDot.x) * x + (topDot.y - startDot.y) * y) / (d);
    x = (x * t + startDot.x);
    y = (y * t + startDot.y);

    const retPoint: marietta.core.Point = new marietta.core.Point(x, y);
    if ((t > 0.0) && (t < 1.0)) {
      return retPoint;
    }
    return null;
  }

  /**
     * 10進数 → 16進数(string)に変換
     * @param 10進数値
     * @return string
     * */
  static convertNum10To16(num: number): string {
    let result: string = num.toString(16);
    // 6桁になるよう'0'を加える
    const count: number = 6 - result.length;
    for (let i = 0; i < count; i += 1) {
      result = `0${result}`;
    }
    result = `#${result}`;
    return result;
  }

  /**
     * 16進数 → 10進数(number)に変換
     * @param 10進数値
     * @return number
     * */
  static convertNum16To10(colr: string): number {
    let color = colr;
    if (color.indexOf('#') > -1) {
      // eslint-disable-next-line prefer-destructuring
      color = color.split('#')[1];
    }
    const result = parseInt(color, 16);
    return result;
  }

  /**
     * 頂点間の角度を取得
     * @param Point 1点目
     * @param Point 2点目
     * @return Number 角度
     * */
  static getAngleBetweenPoints(p1: marietta.core.Point, p2: marietta.core.Point): number {
    const haba: number = p2.x - p1.x;
    const dep: number = p2.y - p1.y;

    return Math.atan2(dep, haba) * (180 / Math.PI);
  }

  /**
     * 頂点座標が時計回りか反時計回りか
     * @param marietta.webcad.collection.PointCollection[]
     * @return Boolean
     */
  static isClockwise(list: marietta.core.Point[] | marietta.webcad.collection.PointCollection[]): boolean {
    let S: number = 0;
    for (let i: number = 0; i < list.length; i += 1) {
      const a = list[i];
      const b = (i < list.length - 1) ? list[i + 1] : list[0];
      S += a.x * b.y - a.y * b.x;
    }
    return ((S / 2) > 0);
  }

  /**
     * 文字数計算（全角を２、半角を１としてカウント）
     */
  static charCount(str1: string): number {
    let len: number = 0;
    const str: string = escape(str1);
    for (let i = 0; i < str.length; i += 1, len += 1) {
      if (str.charAt(i) === '%') {
        // eslint-disable-next-line no-plusplus
        if (str.charAt(++i) === 'u') {
          i += 3;
          len += 1;
        }
        i += 1;
      }
    }
    return len;
  }

  /**
     * 数字を3桁ごとにカンマで区切る
     */
  static sepThreeDigit(num: any): string {
    if (Number.isNaN(parseInt(num, 10))) return;
    const numString = String(num);
    const len: number = numString.length;
    // recursive
    if (len > 3) {
      return `${marietta.webcad.util.WebCADUtil.sepThreeDigit(numString.substring(0, len - 3))},${numString.substring(len - 3)}`;
    }
    return numString;
  }

  /**
     * 表示中のステージ領域を取得
     */
  static getStageArea() {
    const bound = document.getElementById('webcad2dViewInner').getBoundingClientRect();
    const scrollbarWidth = window.innerWidth - document.body.clientWidth;
    const retObj = {
      x: bound.left,
      y: bound.top,
      width: bound.width - scrollbarWidth,
      height: bound.height - scrollbarWidth,
    };
    return retObj;
  }

  /**
     * 表示中のステージの中心座標を取得
     */
  static getPointStageCenter(): marietta.core.Point {
    const stageBound = document.getElementById('webcad2dViewInner').getBoundingClientRect();
    const layerGroupBound = document.getElementById('layerGroup').getBoundingClientRect();
    const scrollBarWidth: number = parseInt(window.getComputedStyle(<HTMLElement>document.getElementById('webcad2dViewInner')).width, 10) - document.getElementById('webcad2dViewInner').clientWidth;
    const minPoint = new marietta.core.Point((stageBound.left - layerGroupBound.left) / marietta.webcad.WebCADGlobal.scaleSize, (stageBound.top - layerGroupBound.top) / marietta.webcad.WebCADGlobal.scaleSize);
    const maxPoint = new marietta.core.Point(minPoint.x + ((stageBound.width - scrollBarWidth) / marietta.webcad.WebCADGlobal.scaleSize), minPoint.y + ((stageBound.height - scrollBarWidth) / marietta.webcad.WebCADGlobal.scaleSize));
    const retPoint: marietta.core.Point = new marietta.core.Point((maxPoint.x + minPoint.x) / 2, (maxPoint.y + minPoint.y) / 2);

    return retPoint;
  }

  static xGetClosestPointDistance(myPoint: marietta.core.Point, begin: marietta.core.Point, end: marietta.core.Point): number {
    const tarPoint: marietta.core.Point = this.xGetClosestPoint(myPoint, begin, end);
    return marietta.core.Point.distance(myPoint, tarPoint);
  }

  static xGetClosestPoint(myPoint: marietta.core.Point, begin: marietta.core.Point, end: marietta.core.Point): marietta.core.Point {
    const x: number[] = [];
    const y: number[] = [];
    const xFactor = (end.x - begin.x) / 4;
    const yFactor = (end.y - begin.y) / 4;
    x.push(begin.x + xFactor);
    x.push(begin.x + (xFactor * 2));
    x.push(begin.x + (xFactor * 3));
    y.push(begin.y + yFactor);
    y.push(begin.y + (yFactor * 2));
    y.push(begin.y + (yFactor * 3));
    let minDistance = Number.MAX_VALUE;
    let distance = 0;
    let finalX = 0;
    let finaly = 0;
    for (let i = 0; i < x.length; i += 1) {
      const tempPoint = new marietta.core.Point(x[i], y[i]);
      distance = marietta.core.Point.distance(myPoint, tempPoint);
      if (distance < minDistance) {
        minDistance = distance;
        finalX = x[i];
        finaly = y[i];
      }
    }
    return new marietta.core.Point(finalX, finaly);
  }

  /*------------------------------------------------------
    * ２線分の交差判定および交点の計算
    *
    * 注意：線分として扱う
    * 戻り値：交点
    */
  static IntersectionEdge(startPoint: marietta.core.Point, endpoint: marietta.core.Point, pointS: marietta.core.Point, pointE: marietta.core.Point): marietta.core.Point {
    const cr = new marietta.core.Point(0, 0);
    const fa1 = this.getDistancePtoLine(startPoint, endpoint, pointS);
    const fb1 = this.getDistancePtoLine(startPoint, endpoint, pointE);
    const fa2 = this.getDistancePtoLine(pointS, pointE, startPoint);
    const fb2 = this.getDistancePtoLine(pointS, pointE, endpoint);
    const fab1 = fa1 * fb1;
    const fab2 = fa2 * fb2;
    if (Math.abs(fa1 - fb1) < ALMOST_ZERO) {
      /* ２線分が平行 */
      return null;
    }
    if ((fab1 < 0) && (fab2 < 0)) {
      const t = fa1 / (fa1 - fb1);
      cr.x = (pointE.x - pointS.x) * t + pointS.x;
      cr.y = (pointE.y - pointS.y) * t + pointS.y;
      return cr;
    }
    return null;
  }

  /*------------------------------------------------------
   * 点と線分の位置関係。
   *
   * 線分を直線と考えて、点と直線の垂直距離を返す。
   * 始点から終点に向かうベクトルの左側に点があるときプラスの距離、右側はマイナス距離。
   */
  static getDistancePtoLine(startPoint: marietta.core.Point, endpoint: marietta.core.Point, point: marietta.core.Point): number {
    let a = startPoint.y - endpoint.y;
    let b = endpoint.x - startPoint.x;
    const s = Math.sqrt(a * a + b * b);
    let d: number;
    if (s < 0.0001) {
      d = marietta.core.Point.distance(point, startPoint);
    } else {
      a /= s;
      b /= s;
      const c = startPoint.x * (-a) - b * startPoint.y;
      d = a * point.x + b * point.y + c;
    }
    return d;
  }

  /**
   * 頂点リストの型変換
   */
  static convertPointListToRoomDotList(dotList: { x: number, y: number }[]): marietta.webcad.collection.PointCollection[] {
    const pList: marietta.webcad.collection.PointCollection[] = [];
    for (let i: number = 0; i < dotList.length; i += 1) {
      const p = new marietta.webcad.collection.PointCollection((dotList[i].x), (dotList[i].y));
      p.guid = marietta.webcad.WebCADGlobal.getGuid();
      pList.push(p);
    }
    return pList;
  }

  static isPointOnSegment(lineP1: marietta.core.Point, lineP2: marietta.core.Point, p: marietta.core.Point): boolean {
    const a = lineP1.x * (lineP2.y - p.y) + lineP2.x * (p.y - lineP1.y) + p.x * (lineP1.y - lineP2.y);
    return a === 0;
  }

  /**
   * Calculate the square of the distance between a finite line segment and a point
   * @param lx1
   * @param ly1
   * @param ldx
   * @param ldy
   * @param lineLengthSquared
   * @param px
   * @param py
   * @returns
   */
  static distanceSquaredToLineSegment(lx1, ly1, ldx, ldy, lineLengthSquared, px, py) {
    let t; // t===0 at line pt 1 and t ===1 at line pt 2
    if (!lineLengthSquared) {
      // 0-length line segment. Any t will return same result
      t = 0;
    } else {
      t = ((px - lx1) * ldx + (py - ly1) * ldy) / lineLengthSquared;

      if (t < 0) { t = 0; } else if (t > 1) { t = 1; }
    }

    const lx = lx1 + t * ldx;
    const ly = ly1 + t * ldy;
    const dx = px - lx;
    const dy = py - ly;
    return dx * dx + dy * dy;
  }

  /**
   * ポイントを反時計回りに並べ替える
   * @param pList
   * @returns
   */
  static sortListCounterClockwise(pList: marietta.core.Point[]|marietta.webcad.collection.PointCollection[]): marietta.core.Point[]|marietta.webcad.collection.PointCollection[] {
    let S: number = 0;
    for (let i: number = 0; i < pList.length; i += 1) {
      const a = pList[i];
      const b = (i < pList.length - 1) ? pList[i + 1] : pList[0];
      S += a.x * b.y - a.y * b.x;
    }
    if ((S / 2 > 0)) return pList.reverse();
    return pList;
  }

  /**
   * 線に沿って点を見つける
   * @param startPoint
   * @param endPoint
   * @param distance
   */
  static findPointAlongLine(startPoint: marietta.core.Point, endPoint: marietta.core.Point, distance: number): marietta.core.Point {
    const _x = ((endPoint.x - startPoint.x) ** 2);
    const _y = ((endPoint.y - startPoint.y) ** 2);
    const d = Math.sqrt(_x + _y);
    if (d === 0) {
      return new marietta.core.Point(startPoint.x, startPoint.y);
    }
    const t = distance / d;
    const retPoint = new marietta.core.Point((1 - t) * startPoint.x + t * endPoint.x, (1 - t) * startPoint.y + t * endPoint.y);
    return retPoint;
  }

  /**
   *線上に存在するか
   */
  static isOnLine(startDot: marietta.core.Point, endDot: marietta.core.Point, topDot: marietta.core.Point) {
    const dist = marietta.core.Point.distance(startDot, endDot);
    const dist1 = marietta.core.Point.distance(startDot, topDot);
    const dist2 = marietta.core.Point.distance(topDot, endDot);
    // topDotが端点とほぼ同一点の場合は、線上（端点）とみなす
    if (dist1 < this.IS_ON_LINE_ENDPOINT_DISTANCE_THRESHOLD || dist2 < this.IS_ON_LINE_ENDPOINT_DISTANCE_THRESHOLD) return true;
    if (((dist1 + dist2) - dist >= 0) && ((dist1 + dist2) - dist <= this.IS_ON_LINE_DISTANCE_TOLERANCE)) {
      return true;
    }
    return false;
  }

  /**
   * 類似の行かどうかを見つける
   * @param line
   * @param lineList
   * @returns
   */
  static isSimilarLine(line1: any, line2: any) {
    let status = false;
    if ((marietta.core.Point.distance(line2.p1, line1.p1) === 0 && marietta.core.Point.distance(line2.p2, line1.p2) === 0)
      || (marietta.core.Point.distance(line2.p2, line1.p1) === 0 && marietta.core.Point.distance(line2.p1, line1.p2) === 0)) {
      status = true;
    }
    return status;
  }

  static getOffsets(a: marietta.core.Point, b: marietta.core.Point, width: number) {
    const
      dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    const scale = width / (2 * len);
    return {
      x: Math.round(-scale * dy * 10) / 10,
      y: Math.round(scale * dx * 10) / 10,
    };
  }

  /**
     *
     * @param points
     * @param width
     * @returns
     */
  static createPolygonLine(points: marietta.core.Point[], width: number, closed: boolean = false) {
    const polygon: marietta.core.Point[] = []; let prevA = []; let prevB = []; let interA; let interB;
    for (let i = 0, il = points.length - 1; i < il; i += 1) {
      const isFirst = !i;
      const isLast = (i === points.length - 2);

      const off = this.getOffsets(points[i], points[i + 1], width);
      if (Number.isFinite(off.x) && Number.isFinite(off.y)) {
        const p0a = new marietta.core.Point(points[i].x + off.x, points[i].y + off.y);
        const p1a = new marietta.core.Point(points[i + 1].x + off.x, points[i + 1].y + off.y);
        const p0b = new marietta.core.Point(points[i].x - off.x, points[i].y - off.y);
        const p1b = new marietta.core.Point(points[i + 1].x - off.x, points[i + 1].y - off.y);

        if (!isFirst) {
          if (prevA.length === 0) {
            prevA = [p0a, p1a];
          }
          interA = marietta.webcad.util.WebCADUtil.getLineCrossPoint(prevA[0], prevA[1], p0a, p1a);
          if (interA) {
            if (closed) polygon.unshift(interA);
          }
          if (prevB.length === 0) {
            prevB = [p0b, p1b];
          }
          interB = marietta.webcad.util.WebCADUtil.getLineCrossPoint(prevB[0], prevB[1], p0b, p1b);
          if (interB) {
            polygon.push(interB);
          }
        }

        if (isFirst) {
          if (closed) polygon.unshift(p0a);
          polygon.push(p0b);
        }

        if (isLast) {
          if (closed) polygon.unshift(p1a);
          polygon.push(p1b);
        }

        if (!isLast) {
          prevA = [p0a, p1a];
          prevB = [p0b, p1b];
        }
      }
    }
    return polygon;
  }

  /**
     * 線の交点を計算
     * @param la1
     * @param lb1
     * @param la2
     * @param lb2
     */
  static getLineCrossPoint(la1: marietta.core.Point, lb1: marietta.core.Point, la2: marietta.core.Point, lb2: marietta.core.Point): marietta.core.Point {
    try {
      const fa1 = la1.y - lb1.y;
      const fa2 = lb1.x - la1.x;
      const fa3 = lb1.y * la1.x - lb1.x * la1.y;
      const fa4 = lb2.y - la2.y;
      const fa5 = la2.x - lb2.x;
      const fa6 = la2.y * lb2.x - la2.x * lb2.y;
      if (Math.abs(fa1 - fa4) < 0.0 || Math.abs(fa2 - fa5) < 0.0) { return; }
      const fb1 = fa1 * fa5 - fa4 * fa2;
      if (Math.abs(fb1) < 1E-05) { return; }
      // const num8 = (fa1 * fa1 + fa2 * fa2) * (fa4 * fa4 + fa5 * fa5);
      // const num9 = (fb1 * fb1) / num8;
      const stPnt = new marietta.core.Point(-(fa5 * fa3 - fa2 * fa6) / fb1, -(-fa4 * fa3 + fa1 * fa6) / fb1);
      return stPnt;
    } catch (err) {
      console.log(err);
    }
  }

  /**
   * 中心を基準に線を回転
   * @param start
   * @param end
   * @param angle
   * @returns
   */
  static rotateLine(p1: marietta.core.Point, p2: marietta.core.Point, angle: number) {
    const center = marietta.core.Point.interpolate(p1, p2, 0.5);
    const p1x = center.x - ((p1.x - center.x) * Math.cos(angle)) - ((p1.y - center.y) * Math.sin(angle));
    const p1y = center.y - ((p1.x - center.x) * Math.sin(angle)) + ((p1.y - center.y) * Math.cos(angle));
    const p2x = center.x - ((p2.x - center.x) * Math.cos(angle)) - ((p2.y - center.y) * Math.sin(angle));
    const p2y = center.y - ((p2.x - center.x) * Math.sin(angle)) + ((p2.y - center.y) * Math.cos(angle));
    return { start: new marietta.core.Point(p1x, p1y), end: new marietta.core.Point(p2x, p2y) };
  }

  /**
     * 点pが線から事前定義された距離（tolerance）内にあるかどうかをテストします
     * @param l1
     * @param l2
     * @param p
     * @param tolerance
     * @returns
     */
  static isPointInBetweenLine(l1: THREE.Vector2 | marietta.core.Point, l2: THREE.Vector2 | marietta.core.Point, p: THREE.Vector2 | marietta.core.Point, tolerance: number = 5): boolean {
    const distance = Math.abs((p.y - l2.y) * l1.x - (p.x - l2.x) * l1.y + p.x * l2.y - p.y * l2.x) / Math.sqrt(((p.y - l2.y) ** 2) + ((p.x - l2.x) ** 2));
    if (distance > tolerance) return false;
    // test if the point p is between l1 and l2
    const dotproduct = (p.x - l1.x) * (l2.x - l1.x) + (p.y - l1.y) * (l2.y - l1.y);
    if (dotproduct < 0) return false;
    const squaredlengthba = (l2.x - l1.x) * (l2.x - l1.x) + (l2.y - l1.y) * (l2.y - l1.y);
    if (dotproduct > squaredlengthba) return false;

    return true;
  }

  /**
     * 角度と距離でポイントからポイントを取得
     * @param basePoint
     * @param angle
     * @param distance
     */
  static getPointWithAngleAndDistanceFromLine(start: marietta.core.Point, end: marietta.core.Point, angle: number, distance: number): marietta.core.Point {
    const lineAngle = marietta.core.Point.getAngleBetween(start, end);
    const angleNew = (angle + lineAngle) % 360;
    const degreeToRadian = this.degreeToRadian(angleNew);
    const num1 = Math.abs(angleNew - 90.0) >= 0.1 ? (Math.abs(angleNew - 270.0) >= 0.1 ? Math.cos(degreeToRadian) : 0.0) : 0.0;
    const num2 = Math.abs(angleNew - 180.0) >= 0.1 ? (Math.abs(angleNew - 360.0) >= 0.1 ? Math.sin(degreeToRadian) : 0.0) : 0.0;
    const pt = new marietta.core.Point((end.x + num1 * distance), (end.y + num2 * distance));
    return pt;
  }

  static findAdjascentWalls(Point1: marietta.core.Point, Point2: marietta.core.Point, wallListAll: any[]): any {
    const adjascentList: any = [];
    for (let i = 0; i < wallListAll.length; i += 1) {
      if (!(((marietta.core.Point.distance(Point1, wallListAll[i].startDot.point) === 0) && (marietta.core.Point.distance(Point2, wallListAll[i].endDot.point) === 0))
        || ((marietta.core.Point.distance(Point1, wallListAll[i].endDot.point) === 0) && (marietta.core.Point.distance(Point2, wallListAll[i].startDot.point) === 0)))) {
        if ((marietta.core.Point.distance(Point1, wallListAll[i].startDot.point) === 0) || (marietta.core.Point.distance(Point1, wallListAll[i].endDot.point) === 0)) {
          adjascentList.push(wallListAll[i]);
        } else if ((marietta.core.Point.distance(Point2, wallListAll[i].startDot.point) === 0) || (marietta.core.Point.distance(Point2, wallListAll[i].endDot.point) === 0)) {
          adjascentList.push(wallListAll[i]);
        }
      }
    }
    return adjascentList;
  }

  /**
     * 度の角度をラジアンに変換
     * @param angle 変換のための度単位の角度
     * */
  static degreeToRadian(angle: number = 0) {
    return (angle / 180.0) * Math.PI;
  }

  /**
     * 線の交点を計算
     * 2つの線分に対する2つの線のクロスポイント（無限の線を考慮しない）
     * @param la1
     * @param lb1
     * @param la2
     * @param lb2
     */
  static getLineCrossPointInsideLines(la1: marietta.core.Point, lb1: marietta.core.Point, la2: marietta.core.Point, lb2: marietta.core.Point): marietta.core.Point {
    try {
      const eps = 0.01;
      if (la1.equals(la2) || lb1.equals(la2)) {
        return null;
      }
      const fa1 = la1.x - la2.x;
      const fa2 = la2.x - lb2.x;
      const fa3 = la1.x - lb1.x;
      const fb1 = la2.y - lb2.y;
      const fb2 = la1.y - la2.y;
      const fb3 = la1.y - lb1.y;
      const t = ((fa1 * fb1) - (fb2 * fa2)) / ((fa3 * fb1) - (fb3 * fa2));
      const u = ((-fa3 * fb2) - (-fb3 * fa1)) / ((fa3 * fb1) - (fb3 * fa2));
      if (t + eps < 0 || t - eps > 1) {
        return null;
      }
      if (u + eps < 0 || u - eps > 1) {
        return null;
      }
      const x = la1.x + (t * (lb1.x - la1.x));
      const y = la1.y + (t * (lb1.y - la1.y));
      if (Number.isNaN(x) || Number.isNaN(y) || !Number.isFinite(x) || !Number.isFinite(y)) {
        return null;
      }
      const crossPoint = new marietta.core.Point(Number(x.toFixed(2)), Number(y.toFixed(2)));
      return crossPoint;
    } catch (err) {
      return null;
    }
  }

  /**
   * 角度と距離でポイントからポイントを取得
   * @param basePoint
   * @param angle
   * @param distance
   */
  static getPointWithAngleAndDistance(basePoint: marietta.core.Point, angle: number, distance: number): marietta.core.Point {
    const degreeToRadian = this.degreeToRadian(angle);
    const num1 = Math.abs(angle - 90.0) >= 0.1 ? (Math.abs(angle - 270.0) >= 0.1 ? Math.cos(degreeToRadian) : 0.0) : 0.0;
    const num2 = Math.abs(angle - 180.0) >= 0.1 ? (Math.abs(angle - 360.0) >= 0.1 ? Math.sin(degreeToRadian) : 0.0) : 0.0;
    const pt = new marietta.core.Point((basePoint.x + num1 * distance), (basePoint.y + num2 * distance));
    return pt;
  }

  /**
    * 部屋の頂点リストをVector2リストに変換
    */
  static convertDotListToVector2D(dotList: marietta.webcad.collection.PointCollection[]): THREE.Vector2[] {
    const pList: THREE.Vector2[] = [];
    for (let i: number = 0; i < dotList.length; i += 1) {
      const p = new THREE.Vector2(dotList[i].x, dotList[i].y);
      pList.push(p);
    }
    return pList;
  }

  /**
   * ポイントが線上にあるかどうかを確認
   * @param p1 /線ポイント１
   * @param p2 //線ポイント2
   * @param ptc 確認ポイント
   */
  static isPointOnLine(p1: marietta.core.Point, p2: marietta.core.Point, ptc: marietta.core.Point) {
    const crossproduct = (ptc.y - p1.y) * (p2.x - p1.x) - (ptc.x - p1.x) * (p2.y - p1.y);
    if (Math.abs(crossproduct) > 0) { return false; }
    const dotproduct = (ptc.x - p1.x) * (p2.x - p1.x) + (ptc.y - p1.y) * (p2.y - p1.y);
    if (dotproduct < 0) { return false; }
    const squaredlengthba = (p2.x - p1.x) * (p2.x - p1.x) + (p2.y - p1.y) * (p2.y - p1.y);
    if (dotproduct > squaredlengthba) { return false; }

    return true;
  }

  /**
     * スケールポイント
     * @param vect
     * @param num
     * @returns
     */
  public static ScaleDiv(vect: marietta.core.Point, num: number) {
    return new marietta.core.Point(vect.x / num, vect.y / num);
  }

  /**
     * ２直線の関係をチェックする
     * @param l1s
     * @param l1e
     * @param l2s
     * @param l2e
     */
  static CheckLineStatus(l1s: marietta.core.Point, l1e: marietta.core.Point, l2s: marietta.core.Point, l2e: marietta.core.Point): number {
    // 角度や長さに影響されないようベクトルを求めて長さを１にする
    let vec1 = new marietta.core.Point(0, 0);
    let vec2 = new marietta.core.Point(0, 0);

    vec1.x = l1e.x - l1s.x;
    vec1.y = l1e.y - l1s.y;
    vec2.x = l2e.x - l2s.x;
    vec2.y = l2e.y - l2s.y;

    const leng1 = Math.sqrt(vec1.x * vec1.x + vec1.y * vec1.y);
    const leng2 = Math.sqrt(vec2.x * vec2.x + vec2.y * vec2.y);

    // 点を送ってきてもだめ
    if (leng1 < ALMOST_ZERO || leng2 < ALMOST_ZERO) { return RC_CLSTAT.ERROR; }

    vec1 = this.ScaleDiv(vec1, leng1); // これでvec1とvec2の長さは共に１になった
    vec2 = this.ScaleDiv(vec2, leng2); // 平行なら同一ベクトル(又は正反対方向)になっているはず
    // 垂直なら内積が０になっているはず

    // 平行かどうかチェック
    const sabun = new marietta.core.Point(0, 0);
    sabun.x = vec2.x - vec1.x; // ベクトルが同一方向の場合
    sabun.y = vec2.y - vec1.y;
    const sousatu = new marietta.core.Point(0, 0);
    sousatu.x = vec2.x + vec1.x; // ベクトルが逆方向の場合
    sousatu.y = vec2.y + vec1.y;

    if (Math.sqrt(sabun.x * sabun.x + sabun.y * sabun.y) < ALMOST_ZERO
            || Math.sqrt(sousatu.x * sousatu.x + sousatu.y * sousatu.y) < ALMOST_ZERO) { return RC_CLSTAT.PARALLEL; }

    // 垂直かどうかチェック
    const naiseki = vec1.x * vec2.x + vec1.y * vec2.y;

    if ((naiseki < ALMOST_ZERO) && (naiseki > -ALMOST_ZERO)) { return RC_CLSTAT.VERTICAL; }

    // 斜交している
    return RC_CLSTAT.DIAGONAL;
  }

  /**
     * ポイントが多角形内にあるかどうかを確認
     * @param poly
     * @param point
     */
  static isPointInsidePolygon(poly: marietta.core.Point[] | marietta.webcad.collection.PointCollection[], point: marietta.core.Point): boolean {
    let c = false;
    // eslint-disable-next-line no-plusplus
    for (let i = -1, l = poly.length, j = l - 1; ++i < l; j = i) {
      // eslint-disable-next-line no-unused-expressions
      ((poly[i].y <= point.y && point.y < poly[j].y) || (poly[j].y <= point.y && point.y < poly[i].y))
                && (point.x < ((poly[j].x - poly[i].x) * (point.y - poly[i].y)) / (poly[j].y - poly[i].y) + poly[i].x)
                && (c = !c);
    }
    return c;
  }

  /**
   * 指定した座標を含む部屋（Room）を取得する
   * @param point 判定する座標（フロア平面座標系。globalDotListと同一座標系）
   * @param floor 対象フロア
   */
  static getRoomAtPoint(point: marietta.core.Point, floor: number): marietta.webcad.item.Room | null {
    const getRoomsByMode = (modeNo: number): marietta.webcad.item.Room[] => {
      const layer = marietta.webcad.WebCADGlobal.layerGroup.getTypeLayerInFloorLayer(floor, modeNo);
      return layer ? layer.getAllChildren() : [];
    };
    let roomItemList: marietta.webcad.item.Room[] = getRoomsByMode(ITEM_MODE_NO.MODE_ROOMDOWN1_NO);
    roomItemList = roomItemList.concat(getRoomsByMode(ITEM_MODE_NO.MODE_ROOMDOWN2_NO));
    roomItemList = roomItemList.concat(getRoomsByMode(ITEM_MODE_NO.MODE_ROOM_NO));
    for (let i: number = roomItemList.length - 1; i >= 0; i -= 1) {
      const roomDotList = roomItemList[i].itemInfo.globalDotList;
      if (WebCADUtil.isPointInsidePolygon(roomDotList, point)) {
        return roomItemList[i];
      }
    }
    return null;
  }

  /**
   * 多角形の重心を見つける
   * @param pointList
   */
  static getPolygonCentroid(temp: marietta.core.Point[] | marietta.webcad.collection.PointCollection[]): marietta.core.Point {
    const pointList = temp.map((d: marietta.core.Point | marietta.webcad.collection.PointCollection) => d.clone());
    const first: any = pointList[0];
    const last = pointList[pointList.length - 1];
    if (first.x !== last.x || first.y !== last.y) pointList.push(first);
    let twicearea = 0;
    let x = 0;
    let y = 0;
    const npointList = pointList.length;
    let p1;
    let p2;
    let f;
    for (let i = 0, j = npointList - 1; i < npointList; j = i++) {
      p1 = pointList[i]; p2 = pointList[j];
      f = p1.x * p2.y - p2.x * p1.y;
      twicearea += f;
      x += (p1.x + p2.x) * f;
      y += (p1.y + p2.y) * f;
    }
    f = twicearea * 3;
    return new marietta.core.Point(x / f, y / f);
  }

  /**
   * Convert data UrI to blob format
   * @param dataURI
   * @returns
   */
  static dataURItoBlob = (dataURI) => {
    // convert base64/URLEncoded data component to raw binary data held in a string
    let byteString;
    if (dataURI.split(',')[0].indexOf('base64') >= 0) { byteString = atob(dataURI.split(',')[1]); } else { byteString = unescape(dataURI.split(',')[1]); }

    // separate out the mime component
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

    // write the bytes of the string to a typed array
    const ia = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ia], { type: mimeString });
  }

  /**
   * price format
   * @param price
   * @param currency
   */
  static priceFormat(price: number, currency: string = '', decimalFormat: boolean = false) {
    let priceVal = price.toString();
    if (decimalFormat) {
      priceVal = price.toFixed(2);
    }
    const priceFormat = currency + priceVal.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
    return priceFormat;
  }

  /**
     * ポイントが多角形内にあるかどうかを確認
     * @param poly
     * @param point
     */
  static pointIsInsidePolygon(polygon: marietta.core.Point[] | THREE.Vector2[] | marietta.webcad.collection.PointCollection[], point: marietta.core.Point | THREE.Vector2 | marietta.webcad.collection.PointCollection) {
    let intersections = 0; // Number of intersections.
    for (let i = 0; i < polygon.length; i += 1) { // Loop throw all of the polygon sides.
      const startPoint = (polygon[i] instanceof PointCollection) ? (<PointCollection>polygon[i]).point : polygon[i]; // Start point of the side.
      let endPoint = polygon[i + 1] || polygon[0]; // End point of the side.
      endPoint = (endPoint instanceof PointCollection) ? (<PointCollection>endPoint).point : endPoint;

      if (this.isPointOnLine(<Point>startPoint, <Point>endPoint, <Point>point) || this.isOnLine(<Point>startPoint, <Point>endPoint, <Point>point)) {
        return { isInside: false, isOutside: false, isOnBorder: true };
      }
      if (point.y < startPoint.y !== point.y < endPoint.y
                && point.x < ((endPoint.x - startPoint.x) * (point.y - startPoint.y)) / (endPoint.y - startPoint.y) + startPoint.x) {
        intersections += 1;
      }
    }
    return { isInside: intersections % 2 === 1, isOutside: intersections % 2 === 0, isOnBorder: false };
  }

  static checkAllPointsExistInDotList(checkList: marietta.core.Point[] | marietta.webcad.collection.PointCollection[], dotList: marietta.webcad.collection.PointCollection[]) {
    let status = false;
    for (let i = 0; i < checkList.length; i += 1) {
      const point = (checkList[i] instanceof PointCollection) ? (<PointCollection>checkList[i]).point : checkList[i];
      const exist = dotList.find((dot) => Math.round(dot.x) === Math.round(point.x) && Math.round(dot.y) === Math.round(point.y));
      if (exist) {
        status = true;
      } else {
        status = false;
        break;
      }
    }
    status = status && (this.calculateArea(checkList, true) === this.calculateArea(dotList, true));
    return status;
  }

  static isAreaZero(dotList: marietta.core.Point[] | marietta.webcad.collection.PointCollection[]): boolean {
    if (marietta.webcad.util.WebCADUtil.calculateArea(dotList) <= AREA_ZERO) {
      return true;
    }
    return false;
  }

  /**
   * テキスト幅計算
   * @param text
   * @param font
   */
  static calculateTextMetrics(text:string, font:string):TextMetrics {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    // Set the font (must match the font used in your design)
    context.font = font;

    // Measure the text width
    const metrics = context.measureText(text);
    return metrics;
  }

  /**
   * 部屋名一覧
   */
  static getExisitingRoomNames():string[]{
    let itemList: marietta.webcad.item.Room[] = marietta.webcad.WebCADGlobal.itemManager.getItemListByMode(ITEM_MODE_NO.MODE_ROOMDOWN1_NO) as marietta.webcad.item.Room[];
    itemList = itemList.concat(marietta.webcad.WebCADGlobal.itemManager.getItemListByMode(ITEM_MODE_NO.MODE_ROOMDOWN2_NO) as marietta.webcad.item.Room[]);
    itemList = itemList.concat(marietta.webcad.WebCADGlobal.itemManager.getItemListByMode(ITEM_MODE_NO.MODE_ROOM_NO) as marietta.webcad.item.Room[]);

    let list:string[] = [];
    itemList.forEach( item => {
      const name = item.itemInfo.text;
      // if( !list.includes(name) && name !=  '' ){
      if( !list.includes(name) ){
        list.push(name);
      }
    });

    return list
  }
}

export default WebCADUtil;
